import { useCallback, useEffect, useState } from 'react';

export const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2';

export function unwrapResponse(response) {
  const root = response?.data ?? response ?? {};
  return root.data ?? root;
}

export function listFrom(payload, ...keys) {
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  return Array.isArray(payload) ? payload : [];
}

export function useOutreachResource(loader, dependencies = []) {
  const [state, setState] = useState({
    data: null,
    loading: true,
    refreshing: false,
    error: null,
    permissionDenied: false,
    loadedAt: null,
  });

  const load = useCallback(
    async ({ refresh = false } = {}) => {
      setState((current) => ({
        ...current,
        loading: !refresh && !current.data,
        refreshing: refresh,
        error: null,
        permissionDenied: false,
      }));
      try {
        const response = await loader();
        setState({
          data: unwrapResponse(response),
          loading: false,
          refreshing: false,
          error: null,
          permissionDenied: false,
          loadedAt: new Date(),
        });
      } catch (error) {
        const status = error?.response?.status;
        const responseError = error?.response?.data?.error;
        const message =
          (typeof responseError === 'string' ? responseError : responseError?.message) ||
          error?.response?.data?.message ||
          error?.message ||
          'This Outreach view could not be loaded.';
        setState((current) => ({
          ...current,
          loading: false,
          refreshing: false,
          error: message,
          permissionDenied: status === 401 || status === 403,
        }));
      }
    },
    // `loader` is intentionally supplied by the page and is stable across its
    // primitive dependencies. This keeps refresh behavior consistent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    dependencies
  );

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, reload: () => load({ refresh: true }) };
}

export function humanize(value) {
  if (value === null || value === undefined || value === '') return '—';
  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return (
    date.toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }) + ' CT'
  );
}
